import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, lastValueFrom, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BaseService } from '../../../../core/base/base.service';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { MetrikMotifRelationModel } from '../../../../core/models/metrik-motif.model';
import { WeekDayEnum } from '../../../../core/enums/week-day.enum';
import { BasketRowStatusEnum, isUntouchableBasketRowStatus } from '../../../../core/enums/basketRowStatusEnum';
import { ContactTypeEnum } from '../../../../core/enums/contact-type.enum';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { RequestTypeEnum } from '../../../../core/enums/request-type.enum';
import { QuoteStatus } from '../../../../core/enums/quote-status.enum';
import { TicketAttributionEnum } from '../../../../core/enums/ticket-attribution.enum';
import { TicketStateEnum } from '../../../../core/enums/ticket-state.enum';
import { OutOfCatalogStatusEnum } from '../../../../core/enums/ticketStatusEnum';
import { ActivityDistributionEntry } from '../../../../core/interfaces/activity-distribution.interface';
import {
  getDisplayStatusMessage,
  hasUnavailabilityReasons,
} from '../../../../core/interfaces/unavailability-reasons.interface';
import { BasketPrestation, BasketProduct } from '../../../../core/models/basket.model';
import { CorrectiveInterventionModel } from '../../../../core/models/corrective-intervention.model';
import { QuoteModel } from '../../../../core/models/quote-model';
import { SupplierModel } from '../../../../core/models/supplier.model';
import {
  CommentActionModel,
  CommentDataModel,
  CommentTicketEnum,
  TicketBasketModel,
  TicketCommentModel,
  TicketLinePermission,
  TicketMainPurchaseFamily,
  CompetenceHoursPreview,
  CompetenceSchedulePayload,
  TicketModel,
} from '../../../../core/models/ticket.model';
import { AuthService } from '../../../../core/services/auth.service';
import { UserProfileModel } from '../../../../core/models/user-profile.model';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { PermissionMessageService } from '../../../../core/services/permission-message.service';

@Injectable({
  providedIn: 'root',
})
export class TicketService extends BaseService<TicketModel> {
  public readonly = true;
  lastAttributionFilter: TicketAttributionEnum;
  comment_data: CommentDataModel[] = [
    { name: 'Créateur du ticket', type: CommentTicketEnum.TICKET_CREATOR },
    // {name: 'Référent équipement', type: CommentTicketEnum.REFERENT_TECHNIC},
    // {name: 'Référent qualité', type: CommentTicketEnum.REFERENT_QUALITY},
    { name: 'Référent engagement', type: CommentTicketEnum.REFERENT_ENGAGEMENT },
    { name: 'Approvisionneur', type: CommentTicketEnum.PURCHASER },
  ];
  comment_default_action: CommentActionModel[] = [
    { type: CommentTicketEnum.TICKET_CREATOR, visible: true, notification: true },
    // {type: CommentTicketEnum.REFERENT_TECHNIC, visible: true, notification: true},
    { type: CommentTicketEnum.PURCHASER, visible: true, notification: true },
    { type: CommentTicketEnum.REFERENT_ENGAGEMENT, visible: true, notification: true },
  ];
  protected _comment = new BehaviorSubject<TicketCommentModel[]>([]);
  readonly comment$ = this._comment.asObservable();
  protected _quote = new BehaviorSubject<QuoteModel[]>([]);
  readonly quote$ = this._quote.asObservable();

  constructor(
    public http: HttpClient,
    public authService: AuthService,
    private snackbarService: SnackbarService,
    private permissionMessageService: PermissionMessageService,
  ) {
    super(http, 'ticket', authService);

    this.readonly = this.isSupervision();

    // this.authService.currentUser.subscribe(user => {
    //     if (user.contactType?.some(x => x.label === ContactTypeEnum.REFERENT_TECHNIC)) {
    //         this.readonly = false;
    //     }
    // })
  }

  protected _basket = new BehaviorSubject<TicketBasketModel>({ products: [], prestations: [] });
  protected _francoPortInfo = new BehaviorSubject<{ [key: string]: any }>({});
  public currentTicketId: string | null = null;

  readonly basket$ = this._basket.asObservable();
  readonly francoPortInfo$ = this._francoPortInfo.asObservable();

  get basket(): Promise<TicketBasketModel> {
    return lastValueFrom(this.basket$.pipe(take(1)));
  }

  /**
   * @param ticket ticket dont les basketLinePermissions s'appliquent — à passer explicitement
   * quand le panier est chargé avant que le ticket soit publié dans entity$ (cf. findById)
   */
  loadBasketLines(ticketId: string, ticket: TicketModel = this._entity.value): Observable<void> {
    const url = this.baseUrlApi.replace('/ticket', '/ticket-basket-line');
    return this.http.get<ObjectApiInterface<TicketBasketModel>>(`${url}/grouped/${ticketId}`).pipe(
      map(res => {
        this.applyLinePermissions(res.data, ticket);
        this._basket.next(res.data);
      }),
    );
  }

  /**
   * Filet unique des mutations du panier : sans UPDATE_TICKET, l'action est bloquée
   * avant tout appel HTTP et la snackbar explique la permission manquante.
   */
  private blockBasketEditIfMissingPermission(): boolean {
    if (this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_TICKET)) {
      return false;
    }
    this.snackbarService.danger(
      this.permissionMessageService.buildMissingPermissionMessage(ENUM_PERMISSIONS.UPDATE_TICKET),
    );
    return true;
  }

  /**
   * Fusionne les basketLinePermissions du ticket (isEditable/isDeletable)
   * dans chaque item du panier (flat lists + structure groupée).
   * Sans UPDATE_TICKET, toutes les lignes sont forcées en lecture seule
   * (le bandeau de ticket-details explique la permission manquante).
   */
  private applyLinePermissions(basket: TicketBasketModel, ticket: TicketModel): void {
    const canUpdateTicket = this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_TICKET);
    const linePermissions: TicketLinePermission[] = ticket?.permissions?.basketLinePermissions;
    if (!linePermissions?.length && canUpdateTicket) return;

    const permMap = new Map<string, TicketLinePermission>((linePermissions ?? []).map(p => [p._id, p]));

    const applyToItem = (item: any): void => {
      const perm = permMap.get(item._id?.toString());
      item.isEditable = canUpdateTicket && (perm?.isEditable ?? false);
      item.isDeletable = canUpdateTicket && (perm?.isDeletable ?? false);
    };

    // Listes plates
    basket.products?.forEach(applyToItem);
    basket.prestations?.forEach(applyToItem);

    // Structure groupée par fournisseur
    if (basket.groupedBySupplier) {
      Object.values(basket.groupedBySupplier).forEach((supplier: any) => {
        Object.values(supplier.engagements || {}).forEach((engagement: any) => {
          engagement.items?.forEach(applyToItem);
        });
      });
    }
  }

  findAllProductsAndPrestations(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<
    ObjectApiInterface<
      | (BasketProduct & {
          ticketCreatedAt?: Date;
        })[]
      | (BasketPrestation & { ticketCreatedAt?: Date })[]
    >
  > {
    return this.http.get<
      ObjectApiInterface<
        | (BasketProduct & { ticketCreatedAt?: Date })[]
        | (BasketPrestation & {
            ticketCreatedAt?: Date;
          })[]
      >
    >(`${this.baseUrlApi}/list-products-prestations`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  createOutOfCatalogTreatment(_id: string, data: any): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/create-out-of-catalog-treatment/${_id}`, data)
      .pipe(
        switchMap(res => {
          this._entity.next(res.data);
          return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
        }),
      );
  }

  deleteOutOfCatalogTreatment(_id: string): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .delete<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/delete-out-of-catalog-treatment/${_id}`)
      .pipe(
        switchMap(res => {
          this.setEntityAndTicketId(res.data);
          return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
        }),
      );
  }

  giveUp(_id: string): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.get<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/give-up/${_id}`).pipe(
      map(res => {
        this._entity.next(res.data);
        return res;
      }),
    );
  }

  close(_id: string, data: any): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/close/${_id}`, data).pipe(
      switchMap(res => {
        this._entity.next(res.data);
        this._comment.next(res.data?.comments);
        return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
      }),
    );
  }

  disableBasketReference(_id: string, data: any): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/${_id}/disable-basket-reference/`, data)
      .pipe(
        switchMap(res => {
          res.data.isUpdatingLines = true;
          this._entity.next(res.data);
          this._comment.next(res.data?.comments);
          return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
        }),
      );
  }

  displayPrice(): boolean {
    return this.isReferentEngagement() || this.isAppro();
  }

  isApproAndHaveFamilly(ticket: TicketModel) {
    // optional chaining utilisé pour éviter une erreur récente. PRED-1149.
    if (ticket?.categories) {
      return (
        this.authService
          .currentUserValue()
          .technicalCategories?.map(category => category?._id)
          ?.filter(c => ticket.categories.includes(c)).length > 0 &&
        this.authService.currentUserValue().contactType?.some(x => x.label === ContactTypeEnum.APPROVISIONNEUR)
      );
    }
    return false;
  }

  isReferentEngagementAndHaveFamilly(ticket: TicketModel) {
    if (ticket?.categories) {
      return (
        this.authService
          .currentUserValue()
          .technicalCategories?.map(category => category?._id)
          ?.filter(c => ticket.categories.includes(c)).length > 0 &&
        this.authService.currentUserValue().contactType?.some(x => x.label === ContactTypeEnum.REFERENT_ENGAGEMENT)
      );
    }
    return false;
  }

  isAppro() {
    return this.authService.currentUserValue().contactType?.some(x => x.label === ContactTypeEnum.APPROVISIONNEUR);
  }

  isCreator() {
    return this.authService.currentUserValue()._id === this._entity.value?.createdBy?._id;
  }

  isSupervision(): boolean {
    return this.authService.currentUserValue().contactType?.some(x => x.label === ContactTypeEnum.SUPERVISION);
  }

  isReferentEngagement(): boolean {
    return this.authService.currentUserValue().contactType?.some(x => x.label === ContactTypeEnum.REFERENT_ENGAGEMENT);
  }

  hasUnavailableReference(): boolean {
    const basket = this._basket.value;
    return (
      basket.products.some(item => this.isItemBlocking(item)) ||
      basket.prestations.some(item => this.isItemBlocking(item))
    );
  }

  /**
   * Vérifie si le panier contient des références indisponibles (validation STRICTE)
   * Bloque pour TOUTES les indisponibilités y compris les références expirées
   * Utilisé pour la finalisation et l'envoi en autorisation (comportement original)
   * @returns true si au moins une référence est indisponible
   */
  hasUnavailableReferenceStrict(): boolean {
    const basket = this._basket.value;
    return (
      basket.products.some(item => this.isItemUnavailable(item)) ||
      basket.prestations.some(item => this.isItemUnavailable(item))
    );
  }

  /**
   * Vérifie si toutes les références du panier sont expirées
   * @returns true si toutes les références sont expirées (cas limite)
   */
  hasOnlyExpiredReferences(): boolean {
    const basket = this._basket.value;
    const allItems = [...(basket.products || []), ...(basket.prestations || [])].filter(
      item => !isUntouchableBasketRowStatus(item.status),
    );
    if (allItems.length === 0) return false;
    return allItems.every(item => this.isItemExpired(item));
  }

  /**
   * Vérifie si un item est expiré (référence ou contrat)
   * @param item L'item du panier
   * @returns true si l'item est expiré
   */
  private isItemExpired(item: any): boolean {
    const entity = this._entity.value;

    // Vérifier l'expiration du contrat
    if (item.contract?.status === 'EXPIRED') {
      return true;
    }

    // Vérifier l'expiration de la référence (avec un jour de décalage)
    if (item.endValidityDate) {
      const endMoment = moment(item.endValidityDate).startOf('day');
      const todayMoment = moment().startOf('day');

      if (endMoment.isBefore(todayMoment)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Analyse les raisons d'indisponibilité d'un item et retourne les détails
   * @param item L'item du panier à vérifier
   * @returns Objet contenant le statut et le message d'indisponibilité
   */
  private analyzeItemAvailability(item: any): { isUnavailable: boolean; message: string | null } {
    const entity = this._entity.value;
    const establishmentId = entity?.establishment?._id;

    // Si l'item a un statut final, il est considéré comme disponible
    if (isUntouchableBasketRowStatus(item.status)) {
      return { isUnavailable: false, message: null };
    }

    // Vérifier d'abord les unavailabilityReasons (priorité)
    if (item.unavailabilityReasons && hasUnavailabilityReasons(item.unavailabilityReasons)) {
      const messages = getDisplayStatusMessage(item);
      const messageText = messages.length > 0 ? messages.join(', ') : 'Indisponible';
      return { isUnavailable: true, message: messageText };
    }

    // Vérifier le périmètre
    const isOutOfPerimeter = establishmentId
      ? item.isFullPerimeter
        ? !item.contract?.perimeter?.find(x => x.establishmentId === establishmentId)
        : !item.perimeter?.find(x => x.establishmentId === establishmentId)
      : false;

    if (isOutOfPerimeter) {
      let message = '';
      if (item.contract?.genericEngagement?.dataPatrimony === 'Equipement GEN') {
        message = 'Patrimoine inactif définitivement';
      } else if (item.contract?.genericEngagement?.dataPatrimony === 'Bâtiment GEN') {
        message = 'Patrimoine inactif définitivement';
      } else {
        message = item?.tooltip === null || item?.tooltip === '' || !item.tooltip ? 'Hors périmètre' : item?.tooltip;
      }

      return { isUnavailable: true, message: message };
    }

    // Vérifier l'expiration du contrat
    if (item.contract?.status === 'EXPIRED') {
      return { isUnavailable: true, message: 'Contrat expiré' };
    }

    // Vérifier l'expiration de la référence (avec un jour de décalage)
    if (item.endValidityDate) {
      const endMoment = moment(item.endValidityDate).startOf('day');
      const todayMoment = moment().startOf('day');

      if (endMoment.isBefore(todayMoment)) {
        return { isUnavailable: true, message: 'Référence expirée' };
      }
    }

    return { isUnavailable: false, message: null };
  }

  /**
   * Vérifie si un item est indisponible (pour l'AFFICHAGE VISUEL)
   * Retourne true pour les références expirées, suspended, etc.
   * Utilisé pour afficher l'icône d'avertissement et le style rouge
   * @param item L'item du panier à vérifier
   * @returns true si l'item a des raisons d'indisponibilité (affichage uniquement)
   */
  public isItemUnavailable(item: any): boolean {
    return this.analyzeItemAvailability(item).isUnavailable;
  }

  /**
   * Vérifie si un item est BLOQUANT pour la création de commande
   * Contrairement à isItemUnavailable(), les références expirées ne bloquent plus
   * car elles seront automatiquement exclues par le backend
   * @param item L'item du panier à vérifier
   * @returns true uniquement pour les indisponibilités BLOQUANTES (suspended, deleted, outOfPerimeter)
   */
  public isItemBlocking(item: any): boolean {
    const entity = this._entity.value;

    // Si l'item a un statut final, il n'est pas bloquant
    if (isUntouchableBasketRowStatus(item.status)) {
      return false;
    }

    // BLOQUE si unavailabilityReasons (suspended, deleted, outOfPerimeter, etc.)
    if (item.unavailabilityReasons && hasUnavailabilityReasons(item.unavailabilityReasons)) {
      return true;
    }

    // BLOQUE si hors périmètre
    const isOutOfPerimeter = item.isFullPerimeter
      ? !item.contract?.perimeter?.find(x => x.establishmentId === entity.establishment._id)
      : !item.perimeter?.find(x => x.establishmentId === entity.establishment._id);

    if (isOutOfPerimeter) {
      return true;
    }

    // ⚠️ NOUVEAU COMPORTEMENT : Les références expirées ne bloquent plus
    // Elles seront automatiquement exclues côté backend avec le statut UNAVAILABLE
    // On ne retourne donc plus true pour les références expirées

    return false;
  }

  /**
   * Génère le message d'infobulle pour expliquer pourquoi un item est indisponible
   * @param item L'item du panier
   * @returns Le message d'infobulle ou null si l'item est disponible
   */
  public getUnavailabilityTooltip(item: any): string | null {
    return this.analyzeItemAvailability(item).message;
  }

  /**
   * Vérifie si une entité possède un traitement hors catalogue en cours.
   *
   * Cette fonction détermine si une entité a un traitement hors catalogue
   * qui est actuellement actif (ni rejeté, ni finalisé).
   *
   * @param entity - L'entité à vérifier
   * @returns true si l'entité a un traitement hors catalogue actif, false sinon
   */
  hasActiveOutOfCatalogTreatment(entity: any): boolean {
    // Vérifie d'abord que l'entité a une propriété outOfCatalogTreatment
    if (!entity.outOfCatalogTreatment) {
      return false;
    }

    const { status } = entity.outOfCatalogTreatment;

    // Un traitement est considéré comme actif s'il n'est ni rejeté ni finalisé
    const isActive = status !== OutOfCatalogStatusEnum.REJECTED && status !== OutOfCatalogStatusEnum.FINALIZED;

    return isActive;
  }

  hasQuote(entity: any): boolean {
    return entity.quotes.length > 0;
  }

  hasEngagedQuote(entity: any): boolean {
    return entity.quotes.some(item => item.status === QuoteStatus.ENGAGED);
  }

  analyzeQuoteStatuses(entity: any): {
    needsQuoteModal: boolean;
    hasSentQuotes: boolean;
    hasSelectedOrReferencingQuotes: boolean;
  } {
    const quotes = entity.quotes || [];
    const hasSentQuotes = quotes.some(q => q.status === QuoteStatus.SENT);
    const hasSelectedOrReferencingQuotes = quotes.some(
      q => q.status === QuoteStatus.SELECTED || q.status === QuoteStatus.REFERENCING_IN_PROGRESS,
    );
    const hasEngagedQuotes = quotes.some(q => q.status === QuoteStatus.ENGAGED);
    const needsQuoteModal = !hasEngagedQuotes && (hasSentQuotes || hasSelectedOrReferencingQuotes);
    return { needsQuoteModal, hasSentQuotes, hasSelectedOrReferencingQuotes };
  }

  cancelOutOfCatalogWithQuotes(
    _id: string,
    data: {
      action: 'confirm_cancel' | 'delete_demand_and_referencing' | 'delete_demand_keep_referencing';
      justification?: string;
      isGiveUp: boolean;
    },
  ): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/cancel-out-of-catalog-with-quotes/${_id}`, data)
      .pipe(
        switchMap(res => {
          this.setEntityAndTicketId(res.data);
          this._comment.next(res.data?.comments);
          this._quote.next(res.data?.quotes);
          return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
        }),
      );
  }

  addToBasket(
    id: string,
    type: 'products' | 'prestations',
    reference: any,
    basketEntity?: boolean,
    addFromFavoriteBasket?: string,
    entityLabel?: string,
  ) {
    if (this.blockBasketEditIfMissingPermission()) {
      return EMPTY;
    }
    const data = {
      ticketId: id,
      referenceId: reference._id,
      type: type === 'products' ? 'product' : 'prestation',
      quantity: reference.quantity,
      quantityBasketType: basketEntity ? reference.quantity : null,
    };

    const ticketBasketLineUrl = this.baseUrlApi.replace('/ticket', '/ticket-basket-line');

    return this.http.post<ObjectApiInterface<any>>(`${ticketBasketLineUrl}/create`, data).pipe(
      map((res: any) => {
        if (addFromFavoriteBasket === 'addOnlyFavorite' && res.statusCode === 201) {
          this.snackbarService.success(`La référence ${entityLabel} a bien été ajoutée`);
        }
        if (addFromFavoriteBasket === 'addEntireFavorite' && res.statusCode === 201) {
          this.snackbarService.success(`La totalité du favori ${entityLabel} a bien été ajoutée`);
        }
        if (!addFromFavoriteBasket) {
          this.snackbarService.success('Référence ajoutée au panier');
        }
      }),
      switchMap(() => this.findById(id)),
    );
  }

  updateBasketItem(id: string, item: any) {
    if (this.blockBasketEditIfMissingPermission()) {
      return EMPTY;
    }
    const data = {
      quantity: item.quantity,
      quantityBasketType: item.quantityBasketType,
      isUrgent: item.isUrgent,
      status: item.status,
    };

    const basketLineUrl = this.baseUrlApi.replace('/ticket', '/ticket-basket-line');

    return this.http.put<ObjectApiInterface<any>>(`${basketLineUrl}/update/${item._id}`, data).pipe(
      switchMap(() => {
        return this.loadBasketLines(id);
      }),
    );
  }

  async removeItemFromBasket(item: any) {
    const entity = await this.entity;
    this.removeFromBasket(entity._id, item).subscribe();
  }

  removeFromBasket(id: string, article: any) {
    if (this.blockBasketEditIfMissingPermission()) {
      return EMPTY;
    }
    const isProduct = this.isProduct(article);
    const basketValue = this._basket.value;

    const matchesArticle = (x: any) => x._id === article._id || x.referenceId?.toString() === article._id;

    // Récupérer l'_id de la ligne de panier avant de filtrer
    const items: any[] = isProduct ? basketValue.products : basketValue.prestations;
    const basketLine = items.find(x => matchesArticle(x));
    const basketLineId = basketLine?._id || article._id;

    if (isProduct) {
      basketValue.products = basketValue.products.filter(x => !matchesArticle(x));
    } else {
      basketValue.prestations = basketValue.prestations.filter(x => !matchesArticle(x));
    }

    const basketLineUrl = this.baseUrlApi.replace('/ticket', '/ticket-basket-line');

    return this.http.delete<ObjectApiInterface<any>>(`${basketLineUrl}/delete/${basketLineId}`).pipe(
      switchMap(() => {
        return this.loadBasketLines(id);
      }),
    );
  }

  updateBasketQuantity(id: string, article: any, direction: 'minus' | 'plus' | 'change') {
    if (this.blockBasketEditIfMissingPermission()) {
      return;
    }
    const basketValue = this._basket.value;
    const isProduct = this.isProduct(article);

    let finded: any;

    if (isProduct) {
      finded = basketValue.products.find(x => x._id === article._id);
    } else {
      finded = basketValue.prestations.find(x => x._id === article._id);
    }

    if (finded) {
      if (direction === 'minus' && finded.quantity === 1) {
        finded.quantity = 1;
        finded.quantityBasketType = 1;
      }
      if (direction === 'change') {
        finded.quantity = article.quantity;
      } else {
        direction === 'minus' ? finded.quantity-- : finded.quantity++;
        if (direction === 'minus') {
          finded.quantityBasketType === 0 ? (finded.quantityBasketType = 0) : finded.quantityBasketType--;
        }
        if (direction === 'plus' && finded.quantityBasketType !== null) {
          finded.quantityBasketType++;
        }
      }
    }

    return this.updateBasketItem(id, finded);

    // return this.http.put<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/${id}/${'update-basket-item'}`, finded).pipe(map(res => {
    //   if (isProduct) {
    //     basketValue.prestations.map(prestation => {
    //       const foundPrestation = res.data?.basket?.prestations.find(x => x._id === prestation._id);
    //       return foundPrestation ? foundPrestation : prestation;
    //     });
    //   } else {
    //     basketValue.products.map(product => {
    //       const foundPrestation = res.data?.basket?.products.find(x => x._id === product._id);
    //       return foundPrestation ? foundPrestation : product;
    //     });
    //   }
    //   this._basket.next(basketValue);
    // }));
  }

  findById(id: string): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.get<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/get/${id}`).pipe(
      // PRED-3266 : charger le panier AVANT de publier le ticket. Les abonnés de entity$ lisent
      // _basket.value (ex. snackbar des références indisponibles de ticket-details) : publié en
      // premier, le ticket était évalué contre le panier du ticket précédemment ouvert.
      switchMap(x => this.loadBasketLines(id, x.data).pipe(map(() => x))),
      map(x => {
        this._comment.next(x.data.comments);
        this._quote.next(x.data.quotes);
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  clearComment() {
    this._comment.next([]);
  }

  addIntervention(intervention: CorrectiveInterventionModel) {
    const value = this._entity.value;
    value.correctiveInterventions.push(intervention);
    this._entity.next(value);
  }

  checkInterventionConfirmation(data: TicketModel) {
    if (!data.genericEquipment || !data.description) {
      return false;
    }

    if (!data.establishment && !data.locationDescription) {
      return false;
    }

    if (!data.establishment && !data.equipment && !data.locationDescription) {
      return false;
    }
    return true;
  }

  openCommentUpdateForm(_id: string) {
    let values = this._comment.value;
    values = values.map(x => {
      if (x._id !== _id) {
        x.isUpdateFormOpen = false;
      } else {
        x.isUpdateFormOpen = !x.isUpdateFormOpen;
      }
      return x;
    });
    this._comment.next(values);
    //   this._customerComment.next(values);
  }

  findAllComment(_id: string): Observable<ObjectApiInterface<TicketCommentModel[]>> {
    return this.http.get<ObjectApiInterface<TicketCommentModel[]>>(`${this.baseUrlApi}/comments/${_id}`).pipe(
      map((res: ObjectApiInterface<TicketCommentModel[]>) => {
        res.data = res.data.map(x => {
          x.isUpdateFormOpen = false;
          return x;
        });
        this._comment.next(res.data);
        return res;
      }),
    );
  }

  addComment(_id: string, entity: TicketCommentModel): Observable<ObjectApiInterface<TicketCommentModel>> {
    return this.http.post<ObjectApiInterface<TicketCommentModel>>(`${this.baseUrlApi}/add-comment/${_id}`, entity).pipe(
      map(res => {
        const value: TicketCommentModel[] = this._comment.value;

        // @ts-ignore
        //    res.data.isUpdateFormOpen = false;

        value.unshift(res.data);
        this._comment.next(value);
        return res;
      }),
    );
  }

  withdraw(_id: string, item: any): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/withdraw/${_id}`, item).pipe(
      switchMap(res => {
        this._entity.next(res.data);
        this._comment.next(res.data?.comments);
        return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
      }),
    );
  }

  sendTicketToSupplier(_id: string, supplier: SupplierModel): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/send-supplier/${_id}`, { supplier })
      .pipe(
        map(res => {
          this._entity.next(res.data);
          return res;
        }),
      );
  }

  updateComment(_id: string, entity: TicketCommentModel): Observable<ObjectApiInterface<TicketCommentModel>> {
    return this.http
      .post<ObjectApiInterface<TicketCommentModel>>(`${this.baseUrlApi}/update-comment/${_id}`, entity)
      .pipe(
        map(res => {
          const values: TicketCommentModel[] = this._comment.value;
          res.data.isUpdateFormOpen = false;
          const findedComm = values.findIndex(x => x._id === _id);
          if (findedComm !== -1) {
            values[findedComm] = res.data;
          }
          this._comment.next(values);
          return res;
        }),
      );
  }

  deleteComment(id: string, _idComment: string): Observable<ObjectApiInterface<TicketCommentModel>> {
    return this.http
      .delete<ObjectApiInterface<TicketCommentModel>>(`${this.baseUrlApi}/${id}/delete-comment/${_idComment}`)
      .pipe(
        map(x => {
          let values = this._comment.value;
          values = values.filter(x => x._id !== _idComment);
          this._comment.next(values);
          return x;
        }),
      );
  }

  // createTicketQuote(id: string, data): Observable<ObjectApiInterface<QuoteModel[]>> {
  //   return this.http.post<ObjectApiInterface<QuoteModel[]>>(`${this.baseUrlApi}/${id}/create-ticket-quote`, data).pipe(map((res: ObjectApiInterface<QuoteModel[]>) => {
  //     let value = this._quote.value;
  //     value = [...value, ...res.data];
  //     this._quote.next(value);
  //     return res;
  //   }));
  // }

  deleteEmptyDraft(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrlApi}/deleteEmptyDraft/${id}`);
  }

  createTicketQuote(id: string, data): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/${id}/create-ticket-quote`, data).pipe(
      map((res: ObjectApiInterface<TicketModel>) => {
        this._entity.next(res.data);
        this._quote.next(res.data.quotes);
        return res;
      }),
    );
  }

  updateBasketLineEmergency(_id: string, type: 'product' | 'prestation', article: any) {
    const value = this._basket.value;
    let found: any;
    if (type === 'product') {
      found = value.products.find(x => x._id === article._id);
    } else {
      found = value.prestations.find(x => x._id === article._id);
    }

    if (found) {
      found.isUrgent = article.isUrgent;
      const url = type === 'product' ? 'updateEmergencyProduct' : 'updateEmergencyPrestation';
      this.http.put(`${this.baseUrlApi}/${_id}/${url}`, found).subscribe();
    }

    this._basket.next(value);
  }

  getEquipmentDetailsByTicketId(ticketId: string): Observable<ObjectApiInterface<any>> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/equipment-details/${ticketId}`).pipe(
      map(res => {
        return res;
      }),
    );
  }

  duplicate(_id, state?: TicketStateEnum, confirmed: boolean = false) {
    const options = state
      ? {
          params: new HttpParams().set('filters', JSON.stringify({ filters: state })),
        }
      : {};
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/${_id}/duplicate`, { confirmed }, options);
  }

  generateExcludedReference() {
    const basket = this._basket.value;
    return basket.products
      .filter(x => !x.isEditable)
      .map(x => x._id)
      .concat(basket.prestations.filter(x => !x.isEditable).map(x => x._id));
  }

  isProduct(article: any): boolean {
    const basketValue = this._basket.value;
    const foundProduct = basketValue.products.find(
      x => x._id === article._id || x.referenceId?.toString() === article._id,
    );

    return !!foundProduct;
  }

  updateOutOfCatalogTreatment(ticketId: string, data: any): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .put<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/update-out-of-catalog-treatment/${ticketId}`, data, {
        headers: { 'Error-CRUD': 'UPDATE' },
      })
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  // Nouvelles fonction d'autorisations

  sendToAuthorization(_id, data): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/send-to-authorization/${_id}`, data)
      .pipe(
        switchMap(res => {
          this._entity.next(res.data);
          return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
        }),
      );
  }

  /** Délégataires proposés pour la supervision du panier (superviseurs du poste de dépense, PRED-3240). */
  getDelegationCandidates(_id: string): Observable<ObjectApiInterface<UserProfileModel[]>> {
    return this.http.get<ObjectApiInterface<UserProfileModel[]>>(`${this.baseUrlApi}/delegation-candidates/${_id}`);
  }
  /** Famille d'achat majoritaire du panier — celle qui pilote la résolution des acteurs. Diagnostic Predik. */
  getMainPurchaseFamily(_id: string): Observable<ObjectApiInterface<TicketMainPurchaseFamily | null>> {
    return this.http.get<ObjectApiInterface<TicketMainPurchaseFamily | null>>(
      `${this.baseUrlApi}/main-purchase-family/${_id}`,
    );
  }

  /** L'autorisation, elle, se délègue sous-panier par sous-panier (TicketSubBasketService.delegate). */
  delegateSupervision(_id: string, selectedUserProfileId: string): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<
        ObjectApiInterface<TicketModel>
      >(`${this.baseUrlApi}/delegate-supervision/${_id}/${selectedUserProfileId}`, {})
      .pipe(
        map(res => {
          this._entity.next(res.data);
          return res;
        }),
      );
  }

  authorize(_id): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/authorize/${_id}`, {}).pipe(
      switchMap(res => {
        this._entity.next(res.data);
        return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
      }),
    );
  }

  adjust(_id, comment: string): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/adjust/${_id}`, { comment }).pipe(
      switchMap(res => {
        this._entity.next(res.data);
        this._comment.next(res.data.comments);
        return this.loadBasketLines(res.data._id || _id).pipe(map(() => res));
      }),
    );
  }

  checkSupervisionConfirmation(establishmentId: string, ticketId: string): Observable<boolean> {
    return this.http
      .get<any>(`${this.baseUrlApi}/${ticketId}/check-supervision-confirmation/${establishmentId}`)
      .pipe(map(x => x.data));
  }

  getResolvedSupervisor(
    ticketId: string,
  ): Observable<{ supervisor: any | null; skipSupervision: boolean; autoIdentified: boolean }> {
    return this.http.get<any>(`${this.baseUrlApi}/${ticketId}/resolve-supervisor`).pipe(map(x => x.data));
  }

  sendToSupervisionConfirmation(ticketId, data): Observable<ObjectApiInterface<TicketModel>> {
    return this.http
      .post<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/send-to-supervision-confirmation/${ticketId}`, data)
      .pipe(
        switchMap(res => {
          this._entity.next(res.data);
          return this.loadBasketLines(res.data._id || ticketId).pipe(map(() => res));
        }),
      );
  }

  /**
   * Update prestation heritage data for interventions
   */
  updateBasketPrestationHeritageData(
    ticketId: string,
    prestationId: string,
    heritageData: any,
  ): Observable<ObjectApiInterface<TicketModel>> {
    const data = {
      prestationId,
      heritageData,
    };

    return this.http
      .put<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/${ticketId}/update-prestation-heritage-data`, data)
      .pipe(
        switchMap((res: ObjectApiInterface<TicketModel>) => {
          this._entity.next(res.data);
          return this.loadBasketLines(res.data._id || ticketId).pipe(map(() => res));
        }),
      );
  }

  /**
   * Get current basket value
   */
  getCurrentBasket(): TicketBasketModel {
    return this._basket.getValue();
  }

  /**
   * Update additional fee state (enabled/disabled)
   */
  updateAdditionalFeeState(feeId: string, isEnabled: boolean, ticketId?: string): void {
    const targetTicketId = ticketId || this.currentTicketId;
    if (targetTicketId) {
      this.toggleAdditionalFee(targetTicketId, feeId, isEnabled).subscribe({
        next: () => {
          this.loadBasketLines(targetTicketId).subscribe();
        },
        error: error => {
          console.error('Erreur sauvegarde frais annexe:', error);
        },
      });
    }
  }

  /**
   * Toggle additional fee state via API
   */
  toggleAdditionalFee(ticketId: string, feeId: string, enabled: boolean): Observable<ObjectApiInterface<any>> {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/${ticketId}/toggle-additional-fee`, {
      feeId,
      enabled,
    });
  }

  // Debounce pour les mises à jour de prix de frais annexes
  private feePriceUpdateTimers: Map<string, any> = new Map();
  private readonly FEE_PRICE_UPDATE_DEBOUNCE_MS = 800;

  /**
   * Update additional fee custom price (HT) for "A définir" fees - API call
   */
  private callUpdateAdditionalFeePrice(
    ticketId: string,
    feeId: string,
    priceHT: number,
  ): Observable<ObjectApiInterface<any>> {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/${ticketId}/update-additional-fee-price`, {
      feeId,
      priceHT,
    });
  }

  /**
   * Sauvegarde le prix HT d'un frais annexe "A définir" avec debounce.
   * Met à jour l'état du service (basket) avec la réponse du backend.
   */
  saveAdditionalFeePrice(feeId: string, priceHT: number, ticketId?: string): void {
    const targetTicketId = ticketId || this.currentTicketId;
    if (!targetTicketId) return;

    // Clear du timer précédent pour ce feeId
    if (this.feePriceUpdateTimers.has(feeId)) {
      clearTimeout(this.feePriceUpdateTimers.get(feeId));
    }

    this.feePriceUpdateTimers.set(
      feeId,
      setTimeout(() => {
        this.feePriceUpdateTimers.delete(feeId);

        this.callUpdateAdditionalFeePrice(targetTicketId, feeId, priceHT).subscribe({
          next: () => {
            this.loadBasketLines(targetTicketId).subscribe();
          },
          error: error => {
            console.error('Erreur sauvegarde prix frais annexe:', error);
          },
        });
      }, this.FEE_PRICE_UPDATE_DEBOUNCE_MS),
    );
  }

  /**
   * Update franco de port information
   */
  updateFrancoPortInfo(francoPortInfo: { [key: string]: any }): void {
    this._francoPortInfo.next(francoPortInfo);
  }

  /**
   * Helper method to set entity and update currentTicketId
   */
  private setEntityAndTicketId(ticket: TicketModel): void {
    this._entity.next(ticket);
    this.currentTicketId = ticket?._id || null;
  }

  /**
   * Get franco de port information
   */
  getFrancoPortInfo(): { [key: string]: any } {
    return this._francoPortInfo.getValue();
  }

  /**
   * Get ticket with basket filtering applied
   */
  findOneByIdWithBasketFilter(id: string, basketFilter?: string): Observable<ObjectApiInterface<TicketModel>> {
    const params = new HttpParams().set(
      'filters',
      basketFilter ? JSON.stringify({ basketStatusFilter: basketFilter }) : '{}',
    );

    return this.http.get<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/get/${id}`, { params }).pipe(
      map(x => {
        this.setEntityAndTicketId(x.data);
        return x;
      }),
    );
  }

  /**
   * Get supplier additional info for a ticket
   */
  getSupplierAdditionalInfo(
    ticketId: string,
  ): Observable<
    ObjectApiInterface<{ content: string; targetSuppliers: Array<{ _id: string; label: string }> } | null>
  > {
    return this.http.get<
      ObjectApiInterface<{ content: string; targetSuppliers: Array<{ _id: string; label: string }> } | null>
    >(`${this.baseUrlApi}/${ticketId}/supplier-additional-info`);
  }

  /**
   * Update supplier additional info for a ticket
   */
  updateSupplierAdditionalInfo(
    ticketId: string,
    data: { content?: string; targetSuppliers?: Array<{ _id: string; label: string }> },
  ): Observable<ObjectApiInterface<{ content: string; targetSuppliers: Array<{ _id: string; label: string }> }>> {
    return this.http.put<
      ObjectApiInterface<{ content: string; targetSuppliers: Array<{ _id: string; label: string }> }>
    >(`${this.baseUrlApi}/${ticketId}/supplier-additional-info`, data);
  }

  /**
   * Update activity distribution for a ticket
   */
  updateActivityDistribution(
    ticketId: string,
    payload: { activityDistribution: ActivityDistributionEntry[] },
  ): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.patch<ObjectApiInterface<TicketModel>>(
      `${this.baseUrlApi}/${ticketId}/activity-distribution`,
      payload,
    );
  }

  updateCompetenceMotif(
    ticketId: string,
    payload: {
      requestType: RequestTypeEnum;
      metrikMotif: MetrikMotifRelationModel;
      justification: string;
      periodStart: Date;
      periodEnd: Date;
      workedDays: WeekDayEnum[];
      dailyHours: number;
    },
  ): Observable<ObjectApiInterface<TicketModel>> {
    return this.http.patch<ObjectApiInterface<TicketModel>>(`${this.baseUrlApi}/${ticketId}/competence-motif`, payload);
  }

  /** PRED-3286 : total d'heures calculé par le back (jours fériés inclus) avant enregistrement. */
  previewCompetenceHours(payload: CompetenceSchedulePayload): Observable<ObjectApiInterface<CompetenceHoursPreview>> {
    return this.http.post<ObjectApiInterface<CompetenceHoursPreview>>(
      `${this.baseUrlApi}/competence-hours/preview`,
      payload,
    );
  }
}
